// /admin/preview/:kind/:id — read-only mirror of a user's dashboard.
// Renders the same fields the user sees on /dashboard, but with no
// editing affordances. Includes a back link to the picker.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AdminNav } from '../components/AdminNav';

type YardSize = 'small' | 'medium' | 'large' | 'xl';

const YARD_LABEL: Record<YardSize, string> = {
  small:  'Small  ·  < 1/8 acre',
  medium: 'Medium ·  1/8 – 1/4 acre',
  large:  'Large  ·  1/4 – 1/2 acre',
  xl:     'XL     ·  1/2 acre +',
};

const WEEKDAY_LABEL: Record<string, string> = {
  sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat',
};

interface Availability {
  weekdays: string[];
  windows: string[];
  blackout_dates: string[];
  notes: string;
}
function parseAvail(raw: string | null | undefined): Availability {
  if (!raw) return { weekdays: [], windows: [], blackout_dates: [], notes: '' };
  try {
    const p = JSON.parse(raw) as Partial<Availability>;
    return {
      weekdays: Array.isArray(p.weekdays) ? p.weekdays : [],
      windows: Array.isArray(p.windows) ? p.windows : [],
      blackout_dates: Array.isArray(p.blackout_dates) ? p.blackout_dates : [],
      notes: typeof p.notes === 'string' ? p.notes : '',
    };
  } catch {
    return { weekdays: [], windows: [], blackout_dates: [], notes: '' };
  }
}

interface ResidentTarget {
  id: number; first_name: string; last_name: string; phone: string; email: string | null;
  address: string; city: string; zip: string; county: string;
  status: string; tier: string; score: number; cycle_start: string | null; cycle_end: string | null;
  cycle_number: number; assigned_track: 'mowkin' | 'volunteer' | null;
  yard_size: YardSize | null; availability_json: string | null;
  contact_pref: string | null; access_notes: string | null; gate_code: string | null;
  archived_at: string | null;
  submitter_email?: string; submitter_name?: string;
}
interface VolunteerTarget {
  id: number; first_name: string; last_name: string; email: string; phone: string;
  city: string | null; county: string | null;
  status: string; vetted_at: string | null;
  has_vehicle: string | null; has_own_equipment: string | null; experience: string | null;
  max_yard_size: YardSize | null; availability_json: string | null;
  service_radius_miles: number | null; can_haul: string | null; preferred_county: string | null;
  archived_at: string | null;
  submitter_email?: string; submitter_name?: string;
}
interface ProviderTarget {
  id: number; business_name: string; owner_name: string; email: string; phone: string;
  capacity_per_month: string; status: string;
  max_yard_size: YardSize | null; availability_json: string | null;
  service_radius_miles: number | null; preferred_county: string | null;
  archived_at: string | null;
  submitter_email?: string; submitter_name?: string;
}
interface AssignmentRow {
  id: number; scheduled_date: string; time_window: string | null; status: string;
  admin_notes: string | null; mowkin_notes: string | null; completed_at: string | null;
  resident_id: number; volunteer_id: number | null; provider_id: number | null;
  // joined names — present depending on perspective
  resident_name?: string | null;
  resident_address?: string | null;
  resident_city?: string | null;
  resident_yard_size?: string | null;
  mowkin_label?: string | null;
  mowkin_kind?: 'volunteer' | 'provider' | null;
}
interface MowDaySignup {
  id: number; mow_day_id: number; signed_up_at: string; status: string;
  event_date: string; mow_day_status: string; county: string | null;
}

interface ApiData {
  kind: 'resident' | 'volunteer' | 'provider';
  target: ResidentTarget | VolunteerTarget | ProviderTarget;
  assignments: AssignmentRow[];
  mow_day_signups?: MowDaySignup[];
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    new: 'bg-forest/10 text-forest',
    pending: 'bg-terracotta text-cream',
    approved: 'bg-forest text-cream',
    denied: 'bg-terracotta/20 text-terracotta',
    vetted: 'bg-forest text-cream',
    rejected: 'bg-terracotta/20 text-terracotta',
    inactive: 'bg-forest/10 text-forest/60',
    proposed: 'bg-sand text-forest',
    accepted: 'bg-forest text-cream',
    declined: 'bg-terracotta/20 text-terracotta',
    completed: 'bg-forest text-cream',
    cancelled: 'bg-forest/10 text-forest/60',
    no_show: 'bg-terracotta/20 text-terracotta',
    confirmed: 'bg-forest text-cream',
    open: 'bg-sand text-forest',
    closed: 'bg-forest/10 text-forest/60',
  };
  const cls = map[status] || 'bg-forest/10 text-forest';
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase ${cls}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-3 text-sm py-1.5 border-b border-forest/5 last:border-b-0">
      <div className="text-xs tracking-[0.15em] uppercase text-forest/55">{label}</div>
      <div className="col-span-2 text-forest/85">{value ?? <span className="text-forest/35">—</span>}</div>
    </div>
  );
}

function AvailabilityDisplay({ raw }: { raw: string | null | undefined }) {
  const a = parseAvail(raw);
  const days = a.weekdays.length
    ? a.weekdays.map((d) => WEEKDAY_LABEL[d] || d).join(' · ')
    : '—';
  const wins = a.windows.length ? a.windows.join(' · ') : '—';
  const blackouts = a.blackout_dates.length ? a.blackout_dates.join(', ') : '—';
  return (
    <div className="space-y-1">
      <FieldRow label="Days" value={days} />
      <FieldRow label="Time of day" value={wins} />
      <FieldRow label="Blackout dates" value={blackouts} />
      {a.notes && <FieldRow label="Notes" value={a.notes} />}
    </div>
  );
}

export function AdminPreviewPage() {
  const { kind, id } = useParams<{ kind: string; id: string }>();
  const [data, setData] = useState<ApiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/admin/preview-dashboard?kind=${encodeURIComponent(kind || '')}&id=${encodeURIComponent(id || '')}`,
          { credentials: 'include' },
        );
        const j = await res.json() as { ok: true; data: ApiData } | { ok: false; error: string };
        if (j.ok) setData(j.data);
        else setError(j.error);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Network error');
      } finally {
        setLoading(false);
      }
    })();
  }, [kind, id]);

  if (loading) {
    return (
      <>
        <AdminNav />
        <div className="max-w-4xl mx-auto px-6 py-16 text-forest/60 italic">Loading...</div>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        <AdminNav />
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm">{error || 'Not found.'}</div>
          <Link to="/admin/preview" className="text-terracotta hover:underline mt-4 inline-block">← Back to picker</Link>
        </div>
      </>
    );
  }

  const isResident = data.kind === 'resident';
  const isVolunteer = data.kind === 'volunteer';
  const isProvider = data.kind === 'provider';
  const r = isResident ? data.target as ResidentTarget : null;
  const v = isVolunteer ? data.target as VolunteerTarget : null;
  const p = isProvider ? data.target as ProviderTarget : null;

  const displayName = r
    ? `${r.first_name} ${r.last_name}`
    : v ? `${v.first_name} ${v.last_name}`
    : p ? p.business_name
    : 'Unknown';

  const subline = r
    ? `${r.address}, ${r.city}, ${r.zip}`
    : v ? `${v.city || '—'}, ${v.county || '—'}`
    : p ? p.owner_name
    : '';

  return (
    <>
      <AdminNav />
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
        <div className="bg-sand/40 border-l-4 border-forest p-3 mb-6 flex items-center gap-3 flex-wrap text-sm">
          <span className="text-[10px] tracking-[0.25em] uppercase text-forest font-medium">Admin Preview · Read-only</span>
          <span className="text-forest/70">Viewing as {data.kind} #{data.target.id}</span>
          <Link to="/admin/preview" className="ml-auto text-terracotta hover:underline">← Picker</Link>
          <Link to={`/admin`} className="text-terracotta hover:underline">Tables →</Link>
        </div>

        <div className="text-xs tracking-[0.25em] uppercase text-terracotta mb-3">
          {isResident ? 'Resident' : isVolunteer ? 'Volunteer Mowkin' : 'Pro Mowkin'} · {data.target.status}
        </div>
        <h1
          className="text-4xl text-forest mb-1 font-display"
          style={{ fontVariationSettings: '"opsz" 144' }}
        >
          {displayName}
        </h1>
        <p className="text-forest/70 mb-8">{subline}</p>

        {/* ── Profile ───────────────────────────────────────────── */}
        <section className="bg-cream border border-forest/15 p-5 mb-6">
          <h2 className="text-lg font-display text-forest mb-3">Profile</h2>
          <div className="space-y-1">
            {r && (
              <>
                <FieldRow label="Phone" value={r.phone} />
                <FieldRow label="Email" value={r.email} />
                <FieldRow label="County" value={r.county} />
                <FieldRow label="Score / Tier" value={`${r.score} · ${r.tier}`} />
                <FieldRow label="Cycle" value={
                  r.cycle_end
                    ? `${r.cycle_start || '—'} → ${r.cycle_end} (cycle #${r.cycle_number})`
                    : '—'
                } />
                <FieldRow label="Track" value={r.assigned_track || '—'} />
                <FieldRow label="Submitter" value={r.submitter_email ? `${r.submitter_email}${r.submitter_name ? ` (${r.submitter_name})` : ''}` : '—'} />
                {r.archived_at && <FieldRow label="Archived" value={r.archived_at} />}
              </>
            )}
            {v && (
              <>
                <FieldRow label="Phone" value={v.phone} />
                <FieldRow label="Email" value={v.email} />
                <FieldRow label="County" value={v.county} />
                <FieldRow label="Status" value={<StatusPill status={v.status} />} />
                <FieldRow label="Vetted" value={v.vetted_at} />
                <FieldRow label="Has vehicle" value={v.has_vehicle} />
                <FieldRow label="Has equipment" value={v.has_own_equipment} />
                <FieldRow label="Experience" value={v.experience} />
                <FieldRow label="Submitter" value={v.submitter_email ? `${v.submitter_email}${v.submitter_name ? ` (${v.submitter_name})` : ''}` : '—'} />
                {v.archived_at && <FieldRow label="Archived" value={v.archived_at} />}
              </>
            )}
            {p && (
              <>
                <FieldRow label="Owner" value={p.owner_name} />
                <FieldRow label="Phone" value={p.phone} />
                <FieldRow label="Email" value={p.email} />
                <FieldRow label="Capacity" value={`${p.capacity_per_month} mows/month`} />
                <FieldRow label="Status" value={<StatusPill status={p.status} />} />
                <FieldRow label="Submitter" value={p.submitter_email ? `${p.submitter_email}${p.submitter_name ? ` (${p.submitter_name})` : ''}` : '—'} />
                {p.archived_at && <FieldRow label="Archived" value={p.archived_at} />}
              </>
            )}
          </div>
        </section>

        {/* ── Scheduling preferences ────────────────────────────── */}
        <section className="bg-cream border border-forest/15 p-5 mb-6">
          <h2 className="text-lg font-display text-forest mb-3">Scheduling preferences</h2>
          <div className="space-y-1 mb-4">
            {r && (
              <>
                <FieldRow label="Yard size" value={r.yard_size ? YARD_LABEL[r.yard_size] : '—'} />
                <FieldRow label="Contact" value={r.contact_pref} />
                <FieldRow label="Access notes" value={r.access_notes} />
                <FieldRow label="Gate code" value={r.gate_code ? '•••••' : '—'} />
              </>
            )}
            {v && (
              <>
                <FieldRow label="Max yard" value={v.max_yard_size ? YARD_LABEL[v.max_yard_size] : '—'} />
                <FieldRow label="Radius" value={v.service_radius_miles != null ? `${v.service_radius_miles} mi` : '—'} />
                <FieldRow label="Preferred county" value={v.preferred_county} />
                <FieldRow label="Can haul" value={v.can_haul} />
              </>
            )}
            {p && (
              <>
                <FieldRow label="Max yard" value={p.max_yard_size ? YARD_LABEL[p.max_yard_size] : '—'} />
                <FieldRow label="Radius" value={p.service_radius_miles != null ? `${p.service_radius_miles} mi` : '—'} />
                <FieldRow label="Preferred county" value={p.preferred_county} />
              </>
            )}
          </div>
          <h3 className="text-xs tracking-[0.2em] uppercase text-forest/60 mb-2">Availability</h3>
          <AvailabilityDisplay raw={data.target.availability_json} />
        </section>

        {/* ── Assignments ──────────────────────────────────────── */}
        <section className="bg-cream border border-forest/15 p-5 mb-6">
          <h2 className="text-lg font-display text-forest mb-3">Mow assignments ({data.assignments.length})</h2>
          {data.assignments.length === 0 ? (
            <p className="text-sm text-forest/55 italic">No assignments yet.</p>
          ) : (
            <div className="space-y-2">
              {data.assignments.map((a) => (
                <div key={a.id} className="border border-forest/15 p-3">
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    <StatusPill status={a.status} />
                    <span className="text-sm font-medium text-forest">{a.scheduled_date}</span>
                    {a.time_window && (
                      <span className="text-xs uppercase tracking-[0.15em] text-forest/60">{a.time_window}</span>
                    )}
                    {a.completed_at && (
                      <span className="text-xs text-forest/60 ml-auto">Done {new Date(a.completed_at).toLocaleDateString()}</span>
                    )}
                  </div>
                  {isResident ? (
                    <div className="text-sm text-forest">
                      Mowkin: <span className="font-medium">{a.mowkin_label || 'unassigned'}</span>
                      {a.mowkin_kind && (
                        <span className="ml-2 text-xs uppercase tracking-[0.15em] text-forest/60">{a.mowkin_kind}</span>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-forest">
                      <span className="font-medium">{a.resident_name}</span>
                      <span className="text-forest/65"> · {a.resident_address}, {a.resident_city}</span>
                      {a.resident_yard_size && (
                        <span className="ml-2 text-xs uppercase tracking-[0.15em] text-forest/60">{a.resident_yard_size}</span>
                      )}
                    </div>
                  )}
                  {a.admin_notes && <div className="text-xs italic text-forest/70 mt-1">Admin: {a.admin_notes}</div>}
                  {a.mowkin_notes && <div className="text-xs italic text-forest/70 mt-1">Mowkin: {a.mowkin_notes}</div>}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Volunteer-only: Mow Day signups ──────────────────── */}
        {v && data.mow_day_signups && data.mow_day_signups.length > 0 && (
          <section className="bg-cream border border-forest/15 p-5 mb-6">
            <h2 className="text-lg font-display text-forest mb-3">Mow Day signups ({data.mow_day_signups.length})</h2>
            <div className="space-y-2">
              {data.mow_day_signups.map((s) => (
                <div key={s.id} className="border border-forest/15 p-3 flex items-center gap-3 flex-wrap">
                  <StatusPill status={s.status} />
                  <span className="text-sm font-medium text-forest">{s.event_date}</span>
                  {s.county && <span className="text-xs uppercase tracking-[0.15em] text-forest/60">{s.county}</span>}
                  <span className="ml-auto text-xs text-forest/55">Mow day: {s.mow_day_status}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
